// Copy some functions from https://github.com/chrisveness/geodesy
//

const TO_RADIAN = 0.017453292519943295; // Math.PI / 180
const TO_DEGREE = 57.29577951308232; // 180 / Math.PI
const RADIUS = 6371e3;

// latlon-spherical.js >> distanceTo(point, radius=6371e3)
export const getDistance = (lat1, lon1, lat2, lon2, radius = RADIUS) => {
  const φ1 = lat1 * TO_RADIAN,
    λ1 = lon1 * TO_RADIAN;
  const φ2 = lat2 * TO_RADIAN,
    λ2 = lon2 * TO_RADIAN;
  const Δφ = lat1 !== lat2 ? φ2 - φ1 : 0;
  const Δλ = lon1 !== lon2 ? λ2 - λ1 : 0;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = radius * c;

  return d;
};

/**
 * 두 지점(위경도) 사이의 방위각을 계산
 * latlon-spherical.js >> initialBearingTo(point)
 * 주의! 위경도 값의 유효성에 대해서는 체크하지 않음
 * @param {number} lat1
 * @param {number} lon1
 * @param {number} lat2
 * @param {number} lon2
 * @returns {number} 두 지점(위경도) 사이의 방위각
 *     반환값 범위: -180 < azumuth <= 180 (-180은 0으로 반환)
 *     -45도는 315도를 의미
 *    getAzimuth(0, 0, 1, 0) => 0
 *    getAzimuth(1, 0, 0, 0) => 180
 *    getAzimuth(1, 0, 2, 0) => 0
 */
export const getAzimuth = (lat1, lon1, lat2, lon2) => {
  const φ1 = lat1 * TO_RADIAN;
  const φ2 = lat2 * TO_RADIAN;
  const Δλ = (lon2 - lon1) * TO_RADIAN;

  const x =
    Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const θ = Math.atan2(y, x);
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/atan2
  // x나 y가 Infinity 또는 -Infinity가 아니면, θ는 -π ~ π 사이의 값을 가짐

  let azimuth = θ * TO_DEGREE;

  // floting point 연산 중 발생할 수 있는 미세한 오류를 잡기 위해
  if (azimuth <= -180) {
    azimuth = 0;
  } else if (azimuth > 180) {
    azimuth = 180;
  }

  return azimuth;
};

// latlon-spherical.js >> destinationPoint(distance, bearing, radius=6371e3)
export const getDestination = (
  lat,
  lon,
  distance,
  bearing,
  radius = RADIUS
) => {
  // sinφ2 = sinφ1⋅cosδ + cosφ1⋅sinδ⋅cosθ
  // tanΔλ = sinθ⋅sinδ⋅cosφ1 / cosδ−sinφ1⋅sinφ2
  // see mathforum.org/library/drmath/view/52049.html for derivation

  const δ = distance / radius; // angular distance in radians
  const θ = Number(bearing) * TO_RADIAN;

  const φ1 = lat * TO_RADIAN,
    λ1 = lon * TO_RADIAN;

  const sinφ2 =
    Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ);
  const φ2 = Math.asin(sinφ2);
  const y = Math.sin(θ) * Math.sin(δ) * Math.cos(φ1);
  const x = Math.cos(δ) - Math.sin(φ1) * sinφ2;
  const λ2 = λ1 + Math.atan2(y, x);

  const lat2 = φ2 * TO_DEGREE;
  const lon2 = λ2 * TO_DEGREE;

  return { lat: lat2, lon: lon2 };
};

export const get3dFault_v1 = (lat1, lon1, lat2, lon2, depth, angle) => {
  // angle: 지표로 부터 수직으로 내려온 가상의 선과 단층 사이의 각도(degree)
  //    90 > angle > -90
  //    실제 "경사각"은 반대의 의미이다 (지표와 단층 사이의 각도)
  // 남동쪽이 +, 북서쪽이 -??????
  // 주향은 0 ~ 180
  // 주향이 90도 이상일 때 angle은 어떻게 표현되는가?
  // 일단 90도 이하일 때만 계산하자...

  const bearing = getAzimuth(lat1, lon1, lat2, lon2); // 주향

  const width = Math.tan(angle * TO_RADIAN) * Math.abs(depth);
  // width는 지표로 부터 수직으로 내려온 가상의 선과 단층 하부 끝 사이의 거리를 의미
  // 그래서 angle 값이 45도 단층은 width와 depth가 같고, angle 값이 0도인 단층은 이 값이 0이 되어야 한다.

  // console.log(`bearing: ${bearing}, width: ${width}`);

  // const latRate = bearing / 90;
  // const lonRate = 1. - bearing;

  const point1 = getDestination(lat1, lon1, width * 1000, bearing + 90);
  const point2 = getDestination(lat2, lon2, width * 1000, bearing + 90);
  // console.log(point1, point2);

  const x = [
    [lon1, lon2],
    [point1.lon, point2.lon],
  ];
  const y = [
    [lat1, lat2],
    [point1.lat, point2.lat],
  ];
  const z = [Array(2).fill(0), Array(2).fill(depth)];
  return { x, y, z };
};

/**
 * 주어진 선 정보와 dip, depth 정보로 부터 단층면을 표현
 * 현재 dip과 depth는
 * @param {number[]} lats
 * @param {number[]} lons
 * @param {number} [dip=45] - 경사각 (지표와 단층 사이)
 *     -90 ~ 90 범위의 값을 가짐
 *     +일 경우 동쪽, -일 경우 서쪽으로 기울어지게 그림(90일 경우 의미 없음)
 *     단, 주향이 90도일 경우, +는 남쪽, -는 북쪽으로 기울어지게 그림
 *     [TODO] dip도 구간별로 받아야 할 수 있음 (그래야 U자형 단층 등을 잘 그릴 수 있음...)
 * @param {number} [depth=-20] - 단층 깊이 (음수로 주어져야 함)
 * @returns {{x: Array, y: Array, z: Array}}
 */
export const get3dFault = (lats, lons, dip = 45, depth = -20) => {
  if (Math.abs(dip) > 90) {
    throw new Error('The value of "dip" is invalid');
  }

  if (lats.length !== lons.length) {
    throw new Error("The lats and lons must be lists of the same length.");
  }

  // 주향 이동 단층일 경우
  if (dip === 90 || dip === -90) {
    return {
      x: [lons, lons],
      y: [lats, lats],
      z: [Array(lats.length).fill(0), Array(lats.length).fill(depth)],
    };
  }

  const ugXs = [];
  const ugYs = [];

  for (let i = 1; i < lats.length; ++i) {
    if (lats[i] === null) {
      ugXs.push(null);
      ugYs.push(null);
      continue;
    } else if (lats[i - 1] === null) {
      continue;
    }

    const azimuth = getAzimuth(lats[i - 1], lons[i - 1], lats[i], lons[i]);
    // -180 < azumuth <= 180, 이를 strike로 바꿀 필요가 있음 (0 <= strike < 180)
    const strike = azimuth === 180 ? 0 : azimuth < 0 ? azimuth + 180 : azimuth;

    const faultWidth = Math.abs(Math.tan((90 + dip) * TO_RADIAN) * depth);
    // faultWidth 지표로 부터 수직으로 내려온 가상의 선과 단층 하부 끝 사이의 거리를 의미
    // 그래서 angle 값이 45도 단층은 faultWidth depth가 같고, dip 값이 90도인 단층은 이 값이 0이 되어야 한다.

    // const dipDirection = dip < 0 ? strike - 90 : strike + 90;
    let dipDirection;
    if (strike <= 90) {
      dipDirection = dip < 0 ? strike - 90 : strike + 90;
    } else {
      dipDirection = dip < 0 ? strike + 90 : strike - 90;
    }

    // console.log(
    //   `strike: ${strike.toFixed(1)}`,
    //   `faultWidth: ${faultWidth.toFixed(1)}`,
    //   `dipDirection: ${dipDirection.toFixed(1)}`
    // );

    const ugPoint1 = getDestination(
      lats[i - 1],
      lons[i - 1],
      faultWidth * 1000,
      dipDirection
    );

    const ugPoint2 = getDestination(
      lats[i],
      lons[i],
      faultWidth * 1000,
      dipDirection
    );

    if (i === 1 || ugXs.at(-1) === null) {
      ugXs.push(ugPoint1.lon);
      ugYs.push(ugPoint1.lat);
    } else {
      if (ugXs.at(-1) !== ugPoint1.lon) {
        const previous_underground_x = ugXs.pop();
        ugXs.push((previous_underground_x + ugPoint1.lon) / 2);
      }
      if (ugYs.at(-1) !== ugPoint1.lat) {
        const previous_underground_y = ugYs.pop();
        ugYs.push((previous_underground_y + ugPoint1.lat) / 2);
      }
    }

    ugXs.push(ugPoint2.lon);
    ugYs.push(ugPoint2.lat);
  }

  const x = [lons, ugXs];
  const y = [lats, ugYs];
  const z = [Array(lats.length).fill(0), Array(lats.length).fill(depth)];

  return { x, y, z };
};
